import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Link, useParams, useHistory } from "react-router-dom";

// API
import { getAuctionProduct } from "../api/product";
import { postBid } from "../api/raffle";
import useAuth from "../hooks/useAuth";

// Bootstrap components
import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";

// Custom Components
import { ProductCarousel } from "../components/Carousel/Carousel";
import { FacebookShare, WhatsAppShare } from "../components/ShareButton";
import Timer from "react-compound-timer";
import moment from "moment-timezone";

// Context
import AlertContext from "../context/AlertContext";

// Utils
import formatMoney from "../utils/formatMoney";
import formatShortName from "../utils/formatShortName";
import useForm from "../utils/useForm";
import {
  BID_COMMITMENT_COPY,
  getMinimumNextBid,
  isAuctionClosed,
  isAuctionOpenForBidding,
  mapBidErrorMessage,
} from "../utils/auctionStatus";

// Assets
import WatchIconGeneral from "../assets/images/watch-icon.svg";
import WatchIconCase from "../assets/images/watch-icon-case.svg";
import WatchIconPointer from "../assets/images/watch-icon-pointer.svg";
import WatchIconStrap from "../assets/images/watch-icon-strap.svg";
import ArrowIcon from "../assets/images/arrow-icon--red.svg";

export default function AuctionProduct() {
  const [product, setProduct] = useState(null);
  const [bids, setBids] = useState([]);
  const [count, setCount] = useState(0);
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadingBidSubmission, setLoadingBidSubmission] = useState(false);
  const [error, setError] = useState({ error: false, message: "" });
  const { auth, user, logout } = useAuth();
  let history = useHistory();
  const alert = useContext(AlertContext);
  const timer = useRef();

  // Memoize counter value calculation to prevent unnecessary recalculations
  const counterValue = useMemo(() => {
    if (!product?.fechaFinal) return 0;

    let endTimeMexicoCity;

    if (product?.horaFinal && product?.fechaFinal) {
      // Get the date from fechaFinal and time from horaFinal, both in Mexico City timezone
      const fechaFinalMexico = moment.tz(product.fechaFinal, "America/Mexico_City");
      const horaFinalMexico = moment.tz(product.horaFinal, "America/Mexico_City");

      // Combine the date from fechaFinal with the time from horaFinal
      endTimeMexicoCity = fechaFinalMexico.clone().hour(horaFinalMexico.hour()).minute(horaFinalMexico.minute()).second(horaFinalMexico.second());
    } else {
      // Fallback to original logic
      const dateFromApi = product?.horaFinal || product?.fechaFinal;
      endTimeMexicoCity = moment.utc(dateFromApi).tz("America/Mexico_City");
    }

    const now = moment.tz("America/Mexico_City");
    const duration = endTimeMexicoCity.diff(now, "milliseconds");

    // Debug logging
    console.log("=== AUCTION TIMEZONE DEBUG ===");
    console.log("API horaFinal (UTC):", product?.horaFinal);
    console.log("API fechaFinal (UTC):", product?.fechaFinal);
    console.log("Combined end time in Mexico City:", endTimeMexicoCity.format("YYYY-MM-DD HH:mm:ss Z"));
    console.log("Current time in Mexico City:", now.format("YYYY-MM-DD HH:mm:ss Z"));
    console.log("Duration in milliseconds:", duration);
    console.log("Duration in hours:", duration / (1000 * 60 * 60));
    console.log("=============================");

    return duration;
  }, [product?.fechaFinal, product?.horaFinal]);

  const auctionEnded = useMemo(
    () => isAuctionClosed(product, counterValue),
    [product, counterValue]
  );
  const auctionActive = useMemo(
    () => isAuctionOpenForBidding(product, counterValue),
    [product, counterValue]
  );

  // Memoize safe bids array
  const safeBids = useMemo(
    () => (Array.isArray(bids) ? [...bids] : []),
    [bids]
  );

  const applyAuctionResponse = useCallback((response) => {
    setBids(response.bids || []);
    setProduct(response);
    setCount(getMinimumNextBid(response, response.bids || []));
  }, []);

  const fetchProduct = useCallback(async ({ showLoading = true } = {}) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await getAuctionProduct(id);

      if (!response.error) {
        applyAuctionResponse(response);
      } else if (showLoading) {
        setError({ error: true, message: response.message });
        alert.error(response.message);
      }
      return response;
    } catch (fetchError) {
      console.error("Error fetching product:", fetchError);
      if (showLoading) {
        setError({ error: true, message: "Error al cargar el producto" });
      }
      return null;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [id, alert, applyAuctionResponse]);

  // Fetch product data
  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id, fetchProduct]);

  useEffect(() => {
    if (!product) return;
    setCount(getMinimumNextBid(product, safeBids));
  }, [safeBids, product]);

  const handleIncrement = useCallback(() => {
    if (!loadingBidSubmission && product?.pujaSubsecuente) {
      setCount((prevCount) => prevCount + product.pujaSubsecuente);
    }
  }, [loadingBidSubmission, product?.pujaSubsecuente]);

  const handleDecrement = useCallback(() => {
    if (loadingBidSubmission || !product?.pujaSubsecuente) return;

    const minimumNextBid = getMinimumNextBid(product, safeBids);

    if (count > minimumNextBid) {
      setCount((prevCount) => prevCount - product.pujaSubsecuente);
    }
  }, [loadingBidSubmission, product, safeBids, count]);

  const { inputs, handleChange, resetForm } = useForm({
    user: user?.username || "",
    bid: 0,
  });

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!user?.user?.id || !product?.id) {
        alert.error("Error: Usuario o producto no válido.");
        return;
      }

      setLoadingBidSubmission(true);

      try {
        const response = await postBid(product.id, count, logout);

        if (response?.error) {
          if (response.errorCode === 409) {
            await fetchProduct({ showLoading: false });
          }
          alert.error(mapBidErrorMessage(response));
          return;
        }

        const newBids = response?.doc?.bids || [];
        setProduct(response.doc);
        setBids(newBids);
        setCount(getMinimumNextBid(response.doc, newBids));
        resetForm({ user: user.username });
        alert.success("Puja guardada exitosamente");
      } catch (submitError) {
        console.error("Error submitting bid:", submitError);
        alert.error(mapBidErrorMessage(submitError));
      } finally {
        setLoadingBidSubmission(false);
      }
    },
    [user, product, count, resetForm, alert, logout, fetchProduct]
  );

  // Memoize winner info
  const winner = useMemo(() => {
    if (!auctionEnded || safeBids.length === 0) return null;
    return safeBids[safeBids.length - 1];
  }, [auctionEnded, safeBids]);

  // Initialize dropdowns (memoized to prevent recreating on every render)
  const initDropdowns = useCallback(() => {
    if (loading) return;

    const sectionContainer = document.querySelector(".product__details");
    if (!sectionContainer) return;

    const sections = Array.from(sectionContainer.children);

    sections.forEach((section) => {
      section.addEventListener("click", (event) => {
        const header = event.target.closest(
          ".product__details__section__header"
        );
        if (header) {
          section.classList.toggle("open");
        }
      });
    });
  }, [loading]);

  useEffect(() => {
    if (product && !loading) {
      initDropdowns();
    }
  }, [product, loading, initDropdowns]);

  // Loading state
  if (loading) {
    return (
      <div className="container p-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Cargando...</span>
          </div>
          <p className="mt-2">Cargando producto...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error.error || !product) {
    return (
      <div className="container p-4">
        <div className="alert alert-danger text-center">
          <h4>Error al cargar el producto</h4>
          <p>
            {error.message || "El producto no existe o no está disponible."}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => history.push("/auctions")}
          >
            Volver a subastas
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Loading Overlay */}
      {loadingBidSubmission && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "30px 40px",
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "15px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div
              style={{
                width: 50,
                height: 50,
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #E10009",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            ></div>
            <span
              style={{
                fontWeight: "bold",
                fontSize: "18px",
                color: "#333",
              }}
            >
              Procesando puja...
            </span>
          </div>
        </div>
      )}

      {/* CSS for spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <section className="product">
        <div className="product__container">
          <div className="product__carousel-container col-12 col-md-6">
            {product?.imagenes?.length > 0 && (
              <ProductCarousel
                productName={product?.nombreProducto}
                productImages={product?.imagenes?.map(img => img.image)}
              />
            )}
          </div>

          <div className="product__content col-12 col-md-6">
            <div className="product__content-holder">
              {/* Auction Status */}
              {auctionEnded && (
                <p
                  style={{
                    color: "#fff",
                    fontWeight: "bolder",
                    backgroundColor: "#E10009",
                    padding: ".5rem 0",
                    borderRadius: "20px",
                  }}
                  className="mb-3 text-center"
                >
                  La subasta ha finalizado
                </p>
              )}

              {/* Product Header */}
              <div className="product__header">
                <p className="product__brand">{product?.marca?.nombre}</p>
                {product?.condicion?.nombre && (
                  <p className="product__newModel">
                    <span>Condición: </span>
                    {product.condicion.nombre}
                  </p>
                )}
              </div>

              <p className="product__name">{product?.nombreProducto}</p>

              {/* Winner Display */}
              {winner && (
                <div className="product__winner">
                  <p>
                    🎉 Ganador: 🎉 <span>{formatShortName(winner.usuario)}</span>
                  </p>
                  <a
                    href={`https://wa.me/525568757619?text=${encodeURIComponent(
                      `¡Hola! Soy ${safeBids[safeBids.length - 1]?.usuario
                      }, el ganador de la subasta del ${product?.nombreProducto
                      }. Me gustaría reclamar mi premio. Mi puja ganadora fue de ${formatMoney(
                        safeBids[safeBids.length - 1]?.bid
                      )} MXN.`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className=" fab fa-whatsapp"></i> Comunícate con nosotros
                    para reclamar tu premio
                  </a>
                </div>
              )}

              <p className="product__description">{product?.descripcion}</p>
              <h5>Políticas de funcionamiento de la subasta:</h5>
              <p
                className="my-2"
                style={{ textAlign: "justify", lineHeight: "1.5" }}
              >
                Empieza esta subasta en {formatMoney(product?.pujaInicial)}{" "}
                pesos siguiendo de pujas subsecuentes mínimas de{" "}
                {formatMoney(product?.pujaSubsecuente)} en{" "}
                {formatMoney(product?.pujaSubsecuente)}!! La subasta da incio
                desde este momento y termina el día{" "}
                <span style={{ fontWeight: "bolder" }}>
                  {(() => {
                    if (product?.horaFinal && product?.fechaFinal) {
                      const fechaFinalMexico = moment.tz(product.fechaFinal, "America/Mexico_City");
                      const horaFinalMexico = moment.tz(product.horaFinal, "America/Mexico_City");
                      const endTimeMexicoCity = fechaFinalMexico.clone()
                        .hour(horaFinalMexico.hour())
                        .minute(horaFinalMexico.minute())
                        .second(horaFinalMexico.second());
                      return endTimeMexicoCity.format("DD-MM-YYYY");
                    } else {
                      return moment.tz(product.fechaFinal, "America/Mexico_City").format("DD-MM-YYYY");
                    }
                  })()}{" "}
                </span>
                {""}a las{" "}
                <span style={{ fontWeight: "bolder" }}>
                  {(() => {
                    if (product?.horaFinal && product?.fechaFinal) {
                      const horaFinalMexico = moment.tz(product.horaFinal, "America/Mexico_City");
                      return horaFinalMexico.format("HH:mm:ss");
                    } else {
                      return "22:59:59";
                    }
                  })()}
                </span>{" "}
                hrs. hasta ese tiempo tienen oportunidad de
                pujar, ganara el que tenga la puja más alta, gastos del envío
                por parte del ganador!! suerte a todos, excelente
                reloj!!🍀🍀🍀⌚⌚⌚ si alguien va a pujar es porque se cuenta
                con el dinero en mano!! Si pujas y no cumples se te bloqueara de
                la página ❌❌❌ Suerte y que gane el mejor postor. nos
                contactaremos con el ganador por WhatsApp o comunicarse con
                nosotros al número de whatssap{" "}
                <a
                  href="https://wa.me/525568757619"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  (55 6875 7619)
                </a>{" "}
                para reclamar su reloj ganado en subasta‼️
              </p>
              {/* Timer */}
              <p className="raffle-product-card__timer">
                {auctionActive && (
                  <Timer
                    initialTime={counterValue}
                    direction="backward"
                    ref={timer}
                  >
                    {() => (
                      <>
                        <Timer.Days /> días <Timer.Hours /> horas{" "}
                        <Timer.Minutes /> minutos <Timer.Seconds /> segundos{" "}
                      </>
                    )}
                  </Timer>
                )}
              </p>

              {/* Bids Display */}
              <div className="product__bids__container">
                <h4>
                  {safeBids.length > 0
                    ? "Pujas:"
                    : "No hay pujas hasta el momento"}
                </h4>
                {safeBids.length > 0 && (
                  <div className="bids-list">
                    {[...safeBids]
                      .sort((a, b) => (b?.bid || 0) - (a?.bid || 0))
                      .map((bid, key) => (
                        <div key={key} className="product__bids">
                          <p>
                            <span>Nombre: </span>
                            {formatShortName(bid.usuario)}
                          </p>
                          <p>
                            <span>Puja: </span>
                            {formatMoney(bid.bid)} MXN
                          </p>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Bidding Form */}
              {!auth?.idUser && (
                <p>
                  Debes <Link to="/login">iniciar sesión</Link> o{" "}
                  <Link to="/register">registrarte</Link> para publicar una
                  puja.
                </p>
              )}

              <div className="product__bids__input">
                {auth.idUser && auctionActive && (
                  <Form onSubmit={handleSubmit} id="bid-form">
                    <Row>
                      <Form.Group
                        className="mt-1"
                        controlId="formName"
                        as={Col}
                        xs="6"
                        lg="6"
                      >
                        <Form.Label>Nombre de usuario:</Form.Label>
                        <Form.Control
                          name="user"
                          plaintext
                          value={user?.user?.fullName || ""}
                          readOnly
                        />
                        <Form.Text className="text-muted">
                          Este es tu nombre de usuario
                        </Form.Text>
                      </Form.Group>

                      <Form.Group
                        className="mb-2"
                        controlId="formBid"
                        as={Col}
                        xs="6"
                        lg="6"
                      >
                        <Form.Label>Oferta:</Form.Label>
                        <div className="bid-container">
                          <button
                            type="button"
                            className="counter-action"
                            disabled={
                              loadingBidSubmission ||
                              count <= getMinimumNextBid(product, safeBids)
                            }
                            onClick={handleDecrement}
                          >
                            -
                          </button>
                          <h5 className="form-control">{formatMoney(count)}</h5>
                          <button
                            type="button"
                            className="counter-action"
                            onClick={handleIncrement}
                            disabled={loadingBidSubmission}
                          >
                            +
                          </button>
                        </div>
                      </Form.Group>
                    </Row>

                    <p className="product__bid-commitment text-muted">
                      {BID_COMMITMENT_COPY}
                    </p>

                    <Button
                      className="send"
                      type="submit"
                      disabled={loadingBidSubmission}
                      style={{
                        opacity: loadingBidSubmission ? 0.7 : 1,
                        cursor: loadingBidSubmission
                          ? "not-allowed"
                          : "pointer",
                      }}
                    >
                      {loadingBidSubmission ? "Enviando..." : "Enviar puja"}
                    </Button>
                  </Form>
                )}
              </div>

              {/* Share Section */}
              <div className="product__share">
                <span>Compartir:</span>
                <div className="d-flex ms-2">
                  <FacebookShare
                    url={`https://larelojeriair.com/auction-product/${product?.id}`}
                  />
                  <WhatsAppShare
                    url={`https://larelojeriair.com/auction-product/${product?.id}`}
                  />
                </div>
              </div>

              <p className="product__price">
                {formatMoney(product?.precio)} MXN
              </p>

              <span>Precio del reloj en el mercado</span>

              {/* Product Details */}
              <div className="product__details">
                <div className="product__details__section">
                  <div className="product__details__section__header">
                    <img
                      src={WatchIconGeneral}
                      alt="Información general"
                      className="product__details__section__header__icon"
                    />
                    <p className="product__details__section__header__name">
                      Información general
                    </p>
                    <img
                      src={ArrowIcon}
                      alt="Abrir menú"
                      className="product__details__section__header__drop-icon"
                    />
                  </div>
                  <div className="product__details__content">
                    <div className="product__details__content__section">
                      <p>{product?.informacionGeneral}</p>
                    </div>
                  </div>
                </div>

                <div className="product__details__section">
                  <div className="product__details__section__header">
                    <img
                      src={WatchIconCase}
                      alt="Carcasa"
                      className="product__details__section__header__icon"
                    />
                    <p className="product__details__section__header__name">
                      Carcasa
                    </p>
                    <img
                      src={ArrowIcon}
                      alt="Abrir menú"
                      className="product__details__section__header__drop-icon"
                    />
                  </div>
                  <div className="product__details__content">
                    <div className="product__details__content__section">
                      <p>{product?.carcasa || 'Información no disponible'}</p>
                    </div>
                  </div>
                </div>

                <div className="product__details__section">
                  <div className="product__details__section__header">
                    <img
                      src={WatchIconPointer}
                      alt="Carátula"
                      className="product__details__section__header__icon"
                    />
                    <p className="product__details__section__header__name">
                      Carátula
                    </p>
                    <img
                      src={ArrowIcon}
                      alt="Abrir menú"
                      className="product__details__section__header__drop-icon"
                    />
                  </div>
                  <div className="product__details__content">
                    <div className="product__details__content__section">
                      <p>{product?.caratula || 'Información no disponible'}</p>
                    </div>
                  </div>
                </div>

                <div className="product__details__section">
                  <div className="product__details__section__header">
                    <img
                      src={WatchIconStrap}
                      alt="Correa"
                      className="product__details__section__header__icon"
                    />
                    <p className="product__details__section__header__name">
                      Correa
                    </p>
                    <img
                      src={ArrowIcon}
                      alt="Abrir menú"
                      className="product__details__section__header__drop-icon"
                    />
                  </div>
                  <div className="product__details__content">
                    <div className="product__details__content__section">
                      <p>{product?.correa}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
